import { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function MessagesPanel({ caseId, currentRole }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [caseId]);

  useEffect(load, [load]);
  useEffect(() => endRef.current?.scrollIntoView({ block: "nearest" }), [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/cases/${caseId}/messages`, { body });
      setBody("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Message not sent"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="messages-panel" className="flex flex-col gap-5">
      <div className="max-h-[420px] overflow-y-auto scroll-thin space-y-4 pr-2">
        {messages.map((m) => {
          const mine = m.author_role === currentRole;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
              <div
                className={`max-w-[78%] rounded-2xl px-4 py-3 border ${
                  mine ? "bg-gold/12 border-gold/30" : "bg-white/[0.04] border-white/10"
                }`}
              >
                <p className="text-[11px] uppercase tracking-widest text-[#9CA3AF]">
                  {m.author_name} · {m.author_role} · {dateTime(m.created_at)}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#F3F4F6] whitespace-pre-wrap">{m.body}</p>
              </div>
            </div>
          );
        })}
        {!messages.length ? <p className="text-sm text-[#9CA3AF]">No messages yet — start the conversation below.</p> : null}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="glass rounded-xl p-4 space-y-3">
        <Textarea
          data-testid="message-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a secure message…"
          className="bg-white/5 border-white/15 min-h-[88px]"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280]">Messages are logged against the case file.</p>
          <Button data-testid="message-send-btn" disabled={sending} className="rounded-full bg-gold text-navy hover:bg-gold-dark">
            <Send className="h-3.5 w-3.5 mr-2" /> {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </form>
    </div>
  );
}
